import { Cause, Effect, Exit, Fiber, Ref, Scope } from "effect";
import { QueryWork } from "../query-work.ts";

export interface WorkbenchQueries {
  readonly run: <A, E, R>(operation: Effect.Effect<A, E, R>) => Effect.Effect<A, E, Exclude<R, Scope.Scope>>;
  readonly cancel: Effect.Effect<void>;
}
export const makeWorkbenchQueries = Effect.fn("Frontend.StationWorkbench.queries")(function* (
  root: Scope.Scope, owner: Scope.Scope, failures: Ref.Ref<readonly Cause.Reason<never>[]>
) {
  const queries = yield* QueryWork, current = yield* Ref.make<Scope.Closeable | null>(null);
  const retain = <E>(exit: Exit.Exit<unknown, E>) => Exit.isFailure(exit)
    ? Ref.update(failures, values => [...values, ...exit.cause.reasons.filter(reason => reason._tag === "Die")]) : Effect.void;
  const close = (scope: Scope.Closeable | null) => scope === null ? Effect.void : Effect.gen(function* () {
    const result = yield* Effect.exit(Scope.close(scope, Exit.void)); yield* retain(result); return yield* result;
  });
  const cancel = Ref.getAndSet(current, null).pipe(Effect.flatMap(close));
  yield* Effect.addFinalizer(() => cancel);
  const run = <A, E, R>(operation: Effect.Effect<A, E, R>): Effect.Effect<A, E, Exclude<R, Scope.Scope>> => Effect.uninterruptibleMask(restore => Effect.gen(function* () {
    if (root.state._tag === "Closed" || owner.state._tag === "Closed") return yield* Effect.interrupt;
    const scope = yield* Scope.fork(owner), previous = yield* Ref.getAndSet(current, scope);
    const previousExit = yield* Effect.exit(close(previous));
    if (Exit.isFailure(previousExit)) { yield* close(scope); return yield* Effect.failCause(previousExit.cause); }
    const result = yield* Effect.exit(restore(Effect.gen(function* () {
      const child = yield* Effect.forkIn(queries.observe(Effect.scoped(operation)), scope, { uninterruptible: false });
      return yield* Fiber.join(child);
    })));
    yield* retain(result);
    yield* Ref.update(current, value => value === scope ? null : value);
    const cleanup = yield* Effect.exit(close(scope));
    if (Exit.isFailure(result)) return yield* Effect.failCause(Cause.fromReasons([
      ...result.cause.reasons, ...(Exit.isFailure(cleanup) ? cleanup.cause.reasons : [])
    ]));
    if (Exit.isFailure(cleanup)) return yield* Effect.failCause(cleanup.cause);
    return result.value;
  }));
  return { run, cancel } satisfies WorkbenchQueries;
});
